import { PortableText } from '@portabletext/react'
import { COLORS } from '@repo/design-system/index'
import { QIcon_chevron_bold_right, QIcon_chevron_down, QIcon_chevron_up } from '@repo/icons/index'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import Image from 'next/image'
import { useMemo, useState } from 'react'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_common } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { BLOG_FAQ_SECTION_WITHOUT_SUBMENU_ID, BLOG_FAQ_SECTION_WITH_SUBMENU_ID } from '../../../sanity/groqQueries'
import {
  BLOG_FAQ_SECTION_WITHOUT_SUBMENU_IDResult,
  BLOG_FAQ_SECTION_WITH_SUBMENU_IDResult,
} from '../../../sanity/types'
import LoadingSpinner from '../../common/LoadingSpinner'
import { ptComponents, updateMarksToBreak } from '../../sanity/util'
import { urlFor } from '../event/PlusDesktopSections'
import { MENU_ID } from './type'

const fetchFAQPosts = async (lang: string, form: string, subMenuId) => {
  const queryCond = subMenuId == 0 ? BLOG_FAQ_SECTION_WITHOUT_SUBMENU_ID : BLOG_FAQ_SECTION_WITH_SUBMENU_ID

  const params = {
    lang: lang,
    form: form,
    subMenuId: subMenuId,
  }

  const posts: BLOG_FAQ_SECTION_WITH_SUBMENU_IDResult[] | BLOG_FAQ_SECTION_WITHOUT_SUBMENU_IDResult[] =
    await client.fetch(queryCond, params)

  return posts
}

export interface IPostItem {
  publishedAt: Date
  tagLists: string[]
  isFAQHaveDetail: boolean
  faqSummary: FAQSummary[]
  isPublic: boolean
  metaData: MetaData
  subMenuCategory: SubMenuCategory
  thumbnail: FAQPreviewImage
  subCategory: string
  faqPreviewImage: FAQPreviewImage
  slug: Slug
  subtitle: string
  title: string
  category: string
}

export interface FAQPreviewImage {
  _type: string
  alt: string
  asset: SubMenuCategory
}

export interface SubMenuCategory {
  _ref: string
  _type: string
}

export interface FAQSummary {
  level: number
  _type: string
  style: string
  _key: string
  listItem: string
  markDefs: any[]
  children: Child[]
}

export interface Child {
  _key: string
  _type: string
  marks: any[]
  text: string
}

export interface MetaData {
  _type: string
  ogTitle: string
  ogDescription: string
  metaDescription: string
  metaKeyword: string
  metaTitle: string
}

export interface Slug {
  _type: string
  current: string
}

interface IFAQAccordion {
  postItem: IPostItem
  isNewPortal: boolean
}

const FAQAccordion = ({ postItem, isNewPortal }: IFAQAccordion) => {
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()
  const { t } = useTranslation()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const content = updateMarksToBreak(postItem.faqSummary)

  return (
    <div className="flex flex-col">
      <div
        onClick={() => setIsOpen((prev) => !prev)}
        className="cursor-pointer border-b-1 flex justify-between flex-row items-center"
      >
        <div className="px-[12px] py-[8px] select-none">
          <span className="Body2-14Md sm:Body1-16Md mr-[8px] text-gray-500">Q</span>
          <span
            className={classNames('sm:Body1-16Md text-gray-700', {
              'Body2-14Bd': isOpen,
              'Body2-14Md': !isOpen,
            })}
          >
            {postItem.title}
          </span>
        </div>
        <div className="">
          {isOpen ? <QIcon_chevron_up width={16} height={16} /> : <QIcon_chevron_down width={16} height={16} />}
        </div>
      </div>
      {isOpen && (
        <div className="pt-[12px] px-[12px] pb-[16px] bg-gray-50 gap-y-[12px] flex flex-col relative select-none">
          {postItem?.faqPreviewImage && (
            <Image
              alt={postItem?.faqPreviewImage?.alt}
              className="mx-auto object-cover"
              src={urlFor(postItem?.faqPreviewImage).url()}
              width={664}
              height={320}
              loading="eager"
            />
          )}
          {content && (
            <div className="lists-class mt-[12px] select-text smd:text-[14px]">
              <PortableText value={content} components={ptComponents} />
            </div>
          )}
          {postItem?.isFAQHaveDetail && (
            <div className="mt-[4px] flex justify-end">
              <div
                className=" flex flex-row items-center cursor-pointer"
                onClick={() =>
                  router.push(
                    isNewPortal
                      ? urls.student.newPortal.helpCenter.detail(postItem.slug.current)
                      : urls.student.landing.blog.detail(postItem.slug.current),
                    {
                      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
                      appSendData: { url: getFullUrl(urls.webview.blog.detail(postItem.slug.current)) },
                    }
                  )
                }
              >
                <span className="Body1-16Bd text-purple-500">{t(n_common.btn_detail)}</span>
                <QIcon_chevron_bold_right width={16} height={16} color={COLORS.PURPLE500} className="pb-[2px]" />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
const FAQSection = ({ subMenuId }) => {
  const router = useAppRouter()

  const { data: posts, isLoading }: any = useQuery({
    queryKey: ['posts', router.query.lang, MENU_ID.FAQ, subMenuId],
    queryFn: () => fetchFAQPosts(router.query.lang as string, MENU_ID.FAQ, subMenuId),
  })

  const displayedPosts = posts
  const isNewPortal = useMemo(() => router.asPath.includes('/portal/'), [router.asPath])

  return (
    <>
      <div className="flex w-full max-w-[688px] flex-col justify-center gap-[8px] sm:gap-y-[20px]">
        {isLoading ? (
          <div className="flex h-[400px] w-full items-center justify-center">
            <LoadingSpinner show primary lg padded />
          </div>
        ) : (
          displayedPosts?.map((postItem, i) => {
            return <FAQAccordion key={i} postItem={postItem} isNewPortal={isNewPortal} />
          })
        )}
      </div>
    </>
  )
}
export default FAQSection
